import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Filter, Search } from 'lucide-react';
import ProductCardNew from '../components/ProductCardNew';
import { products } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const Boutique = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const categories = ['all', ...new Set(products.map(p => p.category))];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleAddToCart = (product) => {
    toast({
      title: "Produit ajouté !",
      description: `${product.name} a été ajouté à votre panier.`,
      duration: 3000,
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Boutique Ecopra
          </h1>
          <p className="text-xl">
            Découvrez notre gamme complète de Kits BuVeurdAir
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex items-center gap-2 text-gray-700">
              <Filter size={20} className="text-[#00BCD4]" />
              <span className="font-semibold">Filtrer :</span>
            </div>
            <div className="flex-1 flex flex-col md:flex-row gap-4 w-full md:w-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#00BCD4]" size={18} />
                <Input
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-[#00BCD4]/30 focus:border-[#00BCD4]"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full md:w-[200px] border-[#00BCD4]/30">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {categories.filter(c => c !== 'all').map(category => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Info Auto-installation */}
      <div className="bg-gradient-to-r from-[#7CB342]/10 to-[#00BCD4]/10 border-y border-[#00BCD4]/20">
        <div className="container mx-auto px-4 py-6">
          <div className="max-w-5xl mx-auto flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#7CB342] to-[#00BCD4] flex items-center justify-center text-white text-xl">
              🛠️
            </div>
            <div>
              <h3 className="font-bold text-gray-800 mb-1">Auto-installation possible</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Vous pouvez installer le kit <strong>vous-même</strong> grâce au guide de montage illustré
                fourni avec chaque produit. L'opération est simple, mais nécessite un outillage de base
                (<em>foret étagé, tournevis, cliquet, douilles</em>) et des compétences de base en mécanique.
                La difficulté dépend principalement de l'accès à l'admission d'air de votre véhicule.
                Une installation professionnelle reste possible en option (voir au moment de la commande).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-4 py-12">
        {filteredProducts.length > 0 ? (
          <>
            <div className="mb-6">
              <p className="text-gray-600">
                {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <ProductCardNew
                  key={product.id}
                  product={product}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <div className="text-gray-400 mb-4">
              <Search size={64} className="mx-auto" />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">
              Aucun produit trouvé
            </h3>
            <p className="text-gray-600 mb-6">
              Essayez de modifier vos critères de recherche
            </p>
            <Button
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              Réinitialiser les filtres
            </Button>
          </div>
        )}
      </div>

      {/* Composants du Kit - Photo réelle */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              <span className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] bg-clip-text text-transparent">
                Composants du Kit Ecopra
              </span>
            </h2>
            <p className="text-center text-gray-600 mb-10 text-lg">
              Découvrez les éléments inclus dans votre kit : tubes flexibles en inox, 
              réacteur et raccords en laiton
            </p>
            
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_news-3/artifacts/bao6drt3_les-kits-de-la-societe-ecopra-sont-de-plusieurs-dimensions-et-diametres-selon-les-vehicules-chaudieres-ou-groupes-electrogenes-utilises-en-preparation-des-kits-pour-les-ulm-et-les-aeroglisseurs-photo-lionel-lopes-quintas-1732629440.jpg"
                alt="Composants du Kit Ecopra - Tubes inox, réacteur, raccords"
                className="w-full h-auto"
              />
              <div className="p-6 bg-gradient-to-r from-[#7CB342]/10 to-[#00BCD4]/10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-3xl mb-2">🔩</div>
                    <p className="font-semibold text-gray-800">Tubes flexibles inox</p>
                    <p className="text-sm text-gray-600">Différentes longueurs</p>
                  </div>
                  <div>
                    <div className="text-3xl mb-2">⚙️</div>
                    <p className="font-semibold text-gray-800">Réacteur</p>
                    <p className="text-sm text-gray-600">Cœur du système</p>
                  </div>
                  <div>
                    <div className="text-3xl mb-2">🔧</div>
                    <p className="font-semibold text-gray-800">Raccords laiton</p>
                    <p className="text-sm text-gray-600">Connexions étanches</p>
                  </div>
                  <div>
                    <div className="text-3xl mb-2">💧</div>
                    <p className="font-semibold text-gray-800">Système hygrométrie</p>
                    <p className="text-sm text-gray-600">Agricole/viticole uniquement</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-blue-50 border-l-4 border-[#00BCD4] p-6 rounded-r-lg">
              <p className="text-gray-800">
                <strong>📦 Contenu variable selon le modèle</strong> : Chaque kit (BVA1 à BVA6) est 
                dimensionné spécifiquement pour votre type de véhicule. Les diamètres et longueurs 
                des tubes varient selon l'application (moto, voiture, agricole, industriel).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <div className="bg-gradient-to-br from-[#7CB342]/10 to-[#00BCD4]/10 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Besoin d'aide pour choisir ?
            </h2>
            <p className="text-gray-700 mb-6">
              Notre équipe est là pour vous conseiller et vous aider à sélectionner 
              le Kit BuVeurdAir le plus adapté à votre véhicule.
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-[#7CB342] to-[#00BCD4] hover:from-[#689F38] hover:to-[#0097A7] text-white font-semibold px-8 py-3 shadow-lg hover:shadow-xl transition-all">
                Contact
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Boutique;